// src/pages/Orders/OrderForm/OrderFormSections/SessionSelectionSection.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../../components/common/Button";
import Badge from "../../../../components/common/Badge";
import Modal from "../../../../components/common/Modal";
import {
  FiUsers,
  FiShoppingCart,
  FiDollarSign,
  FiClock,
  FiRefreshCw,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiSearch,
  FiCalendar,
  FiLock,
} from "react-icons/fi";
import { getBranchSessionsThunk } from "../../../../store/thunks/consultationThunks";

const SessionSelectionSection = ({
  selectedSession,
  onSelectSession,
  onDeselectSession,
}) => {
  const dispatch = useDispatch();
  const { selectedBranch } = useSelector((state) => state.branch);
  const { selectedBranch: selectedBranchRateUI } = useSelector(
    (state) => state.rate.ui
  );
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("COMPLETED");
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load sessions when modal opens
  useEffect(() => {
    if (showSessionModal && (selectedBranch || selectedBranchRateUI)) {
      loadSessions();
    }
  }, [showSessionModal, selectedBranch, statusFilter, selectedBranchRateUI]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const result = await dispatch(
        getBranchSessionsThunk({
          branchCode: selectedBranch || selectedBranchRateUI,
          filters: {
            status: statusFilter,
            limit: 50,
            sortBy: "createdAt",
            sortOrder: "desc",
          },
        })
      ).unwrap();

      setSessions(result.data?.sessions || []);
    } catch (error) {
      console.error("Failed to load sessions:", error);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSession = (session) => {
    onSelectSession(session);
    setShowSessionModal(false);
  };

  const filteredSessions = sessions.filter((session) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      session.sessionCode?.toLowerCase().includes(searchLower) ||
      session.guestCustomer?.name?.toLowerCase().includes(searchLower) ||
      session.userId?.name?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FiUsers className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            Consultation Session (Optional)
          </h2>
        </div>
        {selectedSession && (
          <Badge variant="success" size="sm">
            <FiCheckCircle className="w-3 h-3 mr-1" />
            Session Linked
          </Badge>
        )}
      </div>

      {selectedSession ? (
        /* Selected Session Display */
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedSession.sessionCode}
                  </h3>
                  <Badge variant="blue" size="sm">
                    {selectedSession.status}
                  </Badge>
                  <Badge variant="secondary" size="sm">
                    {selectedSession.sessionType}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Customer:</span>{" "}
                    <span className="font-medium">
                      {selectedSession.userId?.name ||
                        selectedSession.guestCustomer?.name ||
                        "Guest"}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Admin:</span>{" "}
                    <span className="font-medium">
                      {selectedSession.primaryAdmin?.adminName}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Branch:</span>{" "}
                    <span className="font-medium">
                      {selectedSession.branchCode}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Date:</span>{" "}
                    <span className="font-medium">
                      {formatDate(selectedSession.startTime)}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={onDeselectSession}
                className="ml-4"
              >
                <FiX className="w-4 h-4 mr-1" />
                Remove
              </Button>
            </div>

            {/* Session Stats */}
            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-blue-300">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
                  <FiShoppingCart className="w-4 h-4" />
                </div>
                <p className="text-xs text-blue-700">Cart Items</p>
                <p className="text-lg font-bold text-blue-900">
                  {selectedSession.outcome?.itemsAddedToCart || 0}
                </p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
                  <FiRefreshCw className="w-4 h-4" />
                </div>
                <p className="text-xs text-blue-700">Exchange</p>
                <p className="text-lg font-bold text-blue-900">
                  {selectedSession.exchangeItems?.filter(
                    (i) => i.customerAccepted
                  ).length || 0}
                </p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
                  <FiDollarSign className="w-4 h-4" />
                </div>
                <p className="text-xs text-blue-700">Cart Value</p>
                <p className="text-lg font-bold text-blue-900">
                  {formatCurrency(
                    selectedSession.priceLock?.lockedPrices?.summary
                      ?.grandTotal ||
                      selectedSession.initialCartSnapshot?.totalValue ||
                      0
                  )}
                </p>
              </div>
            </div>

            {/* Pricing Lock Info */}
            {selectedSession.priceLock?.isLocked && (
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800 flex items-center gap-2">
                <FiLock className="w-3 h-3" />
                <span>
                  Prices locked until{" "}
                  {formatDate(selectedSession.priceLock.lockExpiresAt)}
                </span>
              </div>
            )}

            {/* Price Adjustments */}
            {selectedSession.priceAdjustments?.filter(
              (adj) => adj.status === "ACTIVE"
            ).length > 0 && (
              <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                <FiCheckCircle className="inline w-3 h-3 mr-1" />
                {
                  selectedSession.priceAdjustments.filter(
                    (adj) => adj.status === "ACTIVE"
                  ).length
                }{" "}
                price adjustment(s) applied
              </div>
            )}
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              Need to select a different session?
            </p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setShowSessionModal(true)}
            >
              <FiSearch className="w-4 h-4 mr-1" />
              Choose Another
            </Button>
          </div>
        </div>
      ) : (
        /* No Session Selected */
        <div className="space-y-4">
          <div className="flex items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary hover:bg-gray-50 transition-all">
            <Button
              type="button"
              variant="primary"
              onClick={() => setShowSessionModal(true)}
            >
              <FiSearch className="w-5 h-5 mr-2" />
              Link Consultation Session
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Or create order without session
              </span>
            </div>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex gap-2">
              <FiAlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Why link a session?</p>
                <ul className="space-y-1 text-xs list-disc list-inside">
                  <li>Auto-populate cart items and exchange items</li>
                  <li>Preserve locked pricing and adjustments</li>
                  <li>Track conversion from consultation to order</li>
                  <li>Maintain complete audit trail</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Session Selection Modal */}
      <Modal
        isOpen={showSessionModal}
        closeModal={() => setShowSessionModal(false)}
        title="Select Consultation Session"
        maxWidth="4xl"
      >
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by session code or customer name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Status</option>
              <option value="COMPLETED">Completed</option>
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
            </select>

            <Button
              size="sm"
              variant="outline"
              onClick={loadSessions}
              disabled={loading}
            >
              <FiRefreshCw className={loading ? "animate-spin" : ""} />
            </Button>
          </div>

          {/* Sessions List */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="text-center py-12">
              <FiAlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No sessions found</p>
            </div>
          ) : (
            <div className="max-h-[500px] overflow-y-auto space-y-3">
              {filteredSessions.map((session) => (
                <SessionCard
                  key={session._id}
                  session={session}
                  onSelect={() => handleSelectSession(session)}
                  formatDate={formatDate}
                  formatCurrency={formatCurrency}
                />
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

// Session Card Component
const SessionCard = ({ session, onSelect, formatDate, formatCurrency }) => {
  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: "green",
      PAUSED: "yellow",
      COMPLETED: "blue",
      CONVERTED: "purple",
      CANCELLED: "red",
    };
    return colors[status] || "gray";
  };

  const cartValue =
    session.priceLock?.lockedPrices?.summary?.grandTotal ||
    session.initialCartSnapshot?.totalValue ||
    0;

  const exchangeCount =
    session.exchangeItems?.filter((i) => i.customerAccepted).length || 0;

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-primary hover:bg-gray-50 transition-all cursor-pointer">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-semibold text-gray-900">
              {session.sessionCode}
            </h4>
            <Badge variant={getStatusColor(session.status)} size="sm">
              {session.status}
            </Badge>
            <Badge variant="secondary" size="sm">
              {session.sessionType}
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-600">Customer:</span>{" "}
              <span className="font-medium">
                {session.userId?.name || session.guestCustomer?.name || "Guest"}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Admin:</span>{" "}
              <span className="font-medium">
                {session.primaryAdmin?.adminName}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <FiCalendar className="w-3 h-3 text-gray-500" />
              <span className="text-gray-600 text-xs">
                {formatDate(session.startTime)}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <FiClock className="w-3 h-3 text-gray-500" />
              <span className="text-gray-600 text-xs">
                {Math.floor((session.duration || 0) / 60)}h{" "}
                {(session.duration || 0) % 60}m
              </span>
            </div>
          </div>
        </div>

        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={onSelect}
          className="ml-4"
        >
          Select
        </Button>
      </div>

      {/* Session Stats */}
      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-200">
        <div className="text-center">
          <p className="text-xs text-gray-600">Cart Items</p>
          <p className="text-sm font-semibold text-gray-900">
            {session.outcome?.itemsAddedToCart || 0}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-600">Exchange</p>
          <p className="text-sm font-semibold text-gray-900">{exchangeCount}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-600">Value</p>
          <p className="text-sm font-semibold text-primary">
            {formatCurrency(cartValue)}
          </p>
        </div>
      </div>

      {/* Price Lock Badge */}
      {session.priceLock?.isLocked && (
        <div className="mt-2 flex items-center gap-1 text-xs text-yellow-700 bg-yellow-50 px-2 py-1 rounded">
          <FiLock className="w-3 h-3" />
          <span>Prices Locked</span>
        </div>
      )}
    </div>
  );
};

export default SessionSelectionSection;
